import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import useNotifications from '../hooks/useNotifications';
import './PropertiesPanelModern.css';

// PropertySection component FUERA del componente principal para evitar problemas de hooks
const PropertySection = memo(({ title, icon, children, isOpen = true, onToggle }) => {
  return (
    <div className="property-section-modern">
      <button
        type="button"
        className="property-section-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-2">
          {icon && <span className="text-base">{icon}</span>}
          <span>{title}</span>
        </div>
        <span className="text-gray-500 text-xs" aria-hidden>{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && (
        <div className="property-section-content">
          {children}
        </div>
      )}
    </div>
  );
});

PropertySection.displayName = 'PropertySection';

const PropertiesPanel = memo(({
  selectedObject,
  onUpdateProperty,
  isMobile,
  isTablet,
  screenSize,
  currentSide = 'front',
  isDoubleSided = false,
  onSwitchToFront,
  onSwitchToBack,
  onToggleDoubleSided,

  onClose, // NUEVO: para cerrar modal en móvil
  getActiveCanvas, // NUEVO: para gestor de capas
  onUpdateCode, // NUEVO: para regenerar códigos

  // Props de Data Binding
  datasets = [],
  selectedDataset = null,
  onSelectDataset = () => { },
  datasetFields = [],
  onDatabaseOpen = () => { },
  onRefreshDatasets = () => { },

  updateTrigger,
  fieldConfigs = {},
  currentRecord = null
}) => {
  const { showSuccess, showInfo, showError } = useNotifications();

  // Estado para panel retráctil en desktop/tablet
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Estado de apertura por sección para evitar que la info aparezca parcial
  const [sectionsOpen, setSectionsOpen] = useState({
    layers: true,
    position: true,
    appearance: true,
    image: true,
    text: true,
    dataSource: true,
    actions: true
  });
  const toggleSection = (key) => setSectionsOpen((s) => ({ ...s, [key]: !s[key] }));
  const expandAll = () => setSectionsOpen({ layers: true, position: true, appearance: true, image: true, text: true, dataSource: true, actions: true });
  const collapseAll = () => setSectionsOpen({ layers: false, position: false, appearance: false, image: false, text: false, dataSource: false, actions: false });

  // Info básica del canvas para distribuir mejor el espacio cuando no hay selección
  const isHelperObject = (obj) => obj?.isGridLine || obj?.isRulerLine || obj?.isGuide;
  const getCanvasInfo = useCallback(() => {
    try {
      const c = getActiveCanvas && getActiveCanvas();
      if (!c) return { width: 0, height: 0, count: 0 };
      const count = typeof c.getObjects === 'function' ? c.getObjects().filter(o => !isHelperObject(o)).length : 0;
      const width = c.width || (typeof c.getWidth === 'function' ? c.getWidth() : 0);
      const height = c.height || (typeof c.getHeight === 'function' ? c.getHeight() : 0);
      return { width, height, count };
    } catch {
      return { width: 0, height: 0, count: 0 };
    }
  }, [getActiveCanvas]);

  const [canvasInfo, setCanvasInfo] = useState({ width: 0, height: 0, count: 0 });
  useEffect(() => { setCanvasInfo(getCanvasInfo()); }, [getCanvasInfo, selectedObject, currentSide, isDoubleSided]);

  // Validación robusta del objeto seleccionado - más tolerante
  const isValidFabricObject = useMemo(() => {
    if (!selectedObject || typeof selectedObject !== 'object') return false;
    // Aceptar si tiene .get/.set o al menos una propiedad 'type'
    const hasGet = typeof selectedObject.get === 'function';
    const hasSet = typeof selectedObject.set === 'function';
    const hasTypeProp = typeof selectedObject.type === 'string' || typeof selectedObject.type === 'number';
    return hasGet || hasSet || hasTypeProp;
  }, [selectedObject]);

  // Funciones auxiliares memoizadas
  const safeGetValue = useCallback((property, defaultValue = '') => {
    if (!isValidFabricObject) return defaultValue;
    try {
      if (selectedObject && typeof selectedObject.get === 'function') {
        const val = selectedObject.get(property);
        return val !== undefined && val !== null ? val : defaultValue;
      }
      // Fallback a acceso directo a la propiedad
      const directVal = selectedObject && selectedObject[property];
      return directVal !== undefined && directVal !== null ? directVal : defaultValue;
    } catch (error) {
      console.warn(`Error getting property ${property}:`, error);
      return defaultValue;
    }
  }, [selectedObject, isValidFabricObject, updateTrigger]);

  const safeSetValue = useCallback((property, value) => {
    if (!isValidFabricObject) return false;
    try {
      if (selectedObject && typeof selectedObject.set === 'function') {
        // Manejo especial para texto: asegurar render y salida de edición
        if (property === 'text' && typeof selectedObject.exitEditing === 'function') {
          try { selectedObject.exitEditing(); } catch { }
        }
        const result = selectedObject.set(property, value);
        try {
          selectedObject.setCoords && selectedObject.setCoords();
          if (selectedObject.canvas) {
            // Forzar rerender estable sin interferir con eventos de teclado
            if (typeof selectedObject.canvas.requestRenderAll === 'function') {
              selectedObject.canvas.requestRenderAll();
            } else {
              selectedObject.canvas.renderAll();
            }
          }
        } catch { }
        return result;
      }
      // Fallback asignación directa (como último recurso)
      if (selectedObject) {
        selectedObject[property] = value;
        try {
          if (selectedObject.canvas?.requestRenderAll) selectedObject.canvas.requestRenderAll();
        } catch { }
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error setting property ${property}:`, error);
      showError(`No se pudo actualizar ${property}`);
      return false;
    }
  }, [selectedObject, isValidFabricObject, showError]);

  // Handle input change optimizado - SIN NOTIFICACIÓN DUPLICADA
  const handleInputChange = useCallback((prop, value) => {
    try {
      if (onUpdateProperty) {
        onUpdateProperty(prop, value);
      } else {
        safeSetValue(prop, value);
        if (selectedObject?.canvas) {
          if (typeof selectedObject.canvas.requestRenderAll === 'function') {
            selectedObject.canvas.requestRenderAll();
          } else {
            selectedObject.canvas.renderAll();
          }
        }
      }
    } catch (error) {
      console.error(`Error updating property ${prop}:`, error);
      showError(`Error al actualizar ${prop}: ${error.message}`);
    }
  }, [onUpdateProperty, safeSetValue, selectedObject, showError]);

  // Clases responsivas memoizadas - AHORA UNIFICADAS para usar siempre el diseño moderno
  const containerClasses = useMemo(() => {
    // Delegamos la responsividad al CSS (Media Queries) para mantener el diseño consistente
    return 'properties-panel-modern';
  }, []);

  const textSizes = useMemo(() => {
    if (isMobile) {
      return {
        header: 'text-lg',
        subheader: 'text-sm',
        section: 'text-sm',
        input: 'text-sm'
      };
    } else if (isTablet) {
      return {
        header: 'text-lg',
        subheader: 'text-sm',
        section: 'text-sm',
        input: 'text-sm'
      };
    } else if (screenSize === 'tv') {
      return {
        header: 'text-xl',
        subheader: 'text-base',
        section: 'text-base',
        input: 'text-lg'
      };
    } else {
      return {
        header: 'text-lg',
        subheader: 'text-sm',
        section: 'text-sm',
        input: 'text-sm'
      };
    }
  }, [isMobile, isTablet, screenSize]);

  // Object type memoizado - tolerante
  const objectType = useMemo(() => {
    if (!isValidFabricObject) return 'unknown';
    try {
      if (selectedObject && typeof selectedObject.get === 'function') {
        const t = selectedObject.get('type');
        if (t) return t;
      }
      if (selectedObject && selectedObject.type) return selectedObject.type;
      return 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }, [selectedObject, isValidFabricObject]);

  // Object info memoizado
  const objectInfo = useMemo(() => {
    const types = {
      textbox: { icon: '📝', name: 'Texto' },
      'i-text': { icon: '📝', name: 'Texto' },
      rect: { icon: '▢', name: 'Rectángulo' },
      circle: { icon: '○', name: 'Círculo' },
      line: { icon: '╱', name: 'Línea' },
      polygon: { icon: '⬠', name: 'Forma' },
      group: { icon: '🧩', name: 'Grupo' },
      activeSelection: { icon: '🧩', name: 'Selección múltiple' },
      image: { icon: '🖼️', name: 'Imagen' }
    };

    return types[objectType] || { icon: '🔧', name: objectType };
  }, [objectType]);

  const isTextType = useMemo(() => objectType === 'textbox' || objectType === 'i-text', [objectType]);
  const isShapeType = useMemo(() => ['rect', 'circle', 'line', 'polygon'].includes(objectType), [objectType]);
  const isImageType = useMemo(() => objectType === 'image', [objectType]);

  // ====== GESTOR DE CAPAS (Layers) ======
  const [layers, setLayers] = useState([]); // [{obj, id, name, visible, locked}]
  const [dragIndex, setDragIndex] = useState(null);
  const [selectedLayerIndex, setSelectedLayerIndex] = useState(-1);

  // Utilidades de capas
  const isHelperObjectLocal = (obj) => obj?.isGridLine || obj?.isRulerLine || obj?.isGuide;

  const computeLayersFromCanvas = useCallback((canvas) => {
    if (!canvas) return [];
    const list = canvas.getObjects()
      .filter(o => !isHelperObjectLocal(o))
      .map((o, idx) => ({
        obj: o,
        id: o.layerId || `${o.type || 'obj'}-${idx}`,
        name: o.layerName || (o.text ? (String(o.text).substring(0, 20) || 'Texto') : (o.type || 'objeto')),
        visible: o.visible !== false,
        locked: o.selectable === false,
        z: idx
      }));
    // Convertir a orden top->bottom
    return list.reverse();
  }, []);

  const refreshLayers = useCallback(() => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const newLayers = computeLayersFromCanvas(canvas);
    setLayers(newLayers);
    if (selectedObject) {
      const idxTopToBottom = newLayers.findIndex(l => l.obj === selectedObject);
      setSelectedLayerIndex(idxTopToBottom);
    } else {
      setSelectedLayerIndex(-1);
    }
  }, [getActiveCanvas, computeLayersFromCanvas, selectedObject]);

  // Suscribirse a eventos del canvas para mantener capas sincronizadas
  useEffect(() => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    refreshLayers();

    const rerender = () => { refreshLayers(); setCanvasInfo(getCanvasInfo()); };
    const onSelection = (e) => {
      if (!e || !e.target) { setSelectedLayerIndex(-1); return; }
      const idx = computeLayersFromCanvas(canvas).findIndex(l => l.obj === e.target);
      setSelectedLayerIndex(idx);
    };

    canvas.on('object:added', rerender);
    canvas.on('object:removed', rerender);
    canvas.on('object:modified', rerender);
    canvas.on('selection:created', onSelection);
    canvas.on('selection:updated', onSelection);
    canvas.on('selection:cleared', rerender);
    return () => {
      canvas.off('object:added', rerender);
      canvas.off('object:removed', rerender);
      canvas.off('object:modified', rerender);
      canvas.off('selection:created', onSelection);
      canvas.off('selection:updated', onSelection);
      canvas.off('selection:cleared', rerender);
    };
  }, [getActiveCanvas, refreshLayers, computeLayersFromCanvas, getCanvasInfo]);

  const selectLayer = (index) => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const layer = layers[index];
    if (!layer) return;
    canvas.setActiveObject(layer.obj);
    canvas.renderAll();
    setSelectedLayerIndex(index);
  };

  const toggleVisibility = (index) => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const layer = layers[index];
    if (!layer) return;
    layer.obj.visible = !layer.obj.visible;
    canvas.discardActiveObject();
    canvas.renderAll();
    refreshLayers();
  };

  const toggleLock = (index) => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const layer = layers[index];
    if (!layer) return;
    const locked = !(layer.obj.selectable !== false);
    layer.obj.selectable = !locked;
    layer.obj.evented = !locked;
    canvas.discardActiveObject();
    canvas.renderAll();
    refreshLayers();
  };

  const renameLayer = (index, newName) => {
    const layer = layers[index];
    if (!layer) return;
    layer.obj.layerName = newName;
    setLayers(prev => prev.map((l, i) => i === index ? { ...l, name: newName } : l));
  };

  const duplicateLayer = (index, e) => {
    e.stopPropagation();
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const layer = layers[index];
    if (!layer) return;

    layer.obj.clone((cloned) => {
      canvas.add(cloned.set({
        left: layer.obj.left + 20,
        top: layer.obj.top + 20,
        evented: true
      }));
      canvas.setActiveObject(cloned);
      canvas.renderAll();
      refreshLayers();
    });
  };

  const deleteLayer = (index, e) => {
    e.stopPropagation();
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    const layer = layers[index];
    if (!layer) return;

    canvas.remove(layer.obj);
    canvas.discardActiveObject();
    canvas.renderAll();
    refreshLayers();
  };

  // Drag & Drop para reordenar capas (top->bottom en UI)
  const onDragStart = (e, index) => {
    setDragIndex(index);
    if (e?.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (e?.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };

  const applyOrderToCanvas = (orderedTopToBottom) => {
    const canvas = getActiveCanvas && getActiveCanvas();
    if (!canvas) return;
    // Para fijar el orden, traemos al frente en orden bottom->top
    const bottomToTop = [...orderedTopToBottom].reverse();
    bottomToTop.forEach(layer => {
      try { canvas.bringToFront(layer.obj); } catch { }
    });
    canvas.renderAll();
  };

  const onDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const updated = [...layers];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setLayers(updated);
    setDragIndex(null);
    applyOrderToCanvas(updated);
  };

  // Sección para imágenes: controles extra de códigos (QR/Barcode)
  const renderImageCodeProperties = useCallback(() => {
    if (!isImageType) return null;
    const customType = safeGetValue('customType', selectedObject.customType || '');
    if (customType !== 'qrCode' && customType !== 'barcode1d') return null;

    const transparentBackground = !!(safeGetValue('transparentBackground', selectedObject.transparentBackground));
    const codeType = safeGetValue('codeType', selectedObject.codeType || 'url');
    const codeValue = safeGetValue('codeValue', selectedObject.codeValue || '');
    const codeFormat = safeGetValue('codeFormat', selectedObject.codeFormat || 'CODE128');

    const handleCodeUpdate = (newType, newValue, newFormat) => {
      if (onUpdateCode) {
        onUpdateCode(selectedObject, newType, newValue, newFormat);
      }
    };

    const toggleTransparent = (checked) => {
      try {
        const img = selectedObject;
        const canvas = img && img.canvas;
        if (!img || !canvas) return;
        const el = img._element; // HTMLImageElement subyacente
        if (!el || !el.src) return;
        const baseImg = new Image();
        baseImg.crossOrigin = 'anonymous';
        baseImg.onload = () => {
          const off = document.createElement('canvas');
          off.width = baseImg.naturalWidth || baseImg.width;
          off.height = baseImg.naturalHeight || baseImg.height;
          const ctx = off.getContext('2d');
          ctx.drawImage(baseImg, 0, 0, off.width, off.height);
          if (checked) {
            // Eliminar blanco -> transparente
            const imgData = ctx.getImageData(0, 0, off.width, off.height);
            const data = imgData.data;
            for (let i = 0; i < data.length; i += 4) {
              if (data[i] >= 245 && data[i + 1] >= 245 && data[i + 2] >= 245 && data[i + 3] > 0) {
                data[i + 3] = 0;
              }
            }
            ctx.putImageData(imgData, 0, 0);
          } else {
            // Asegurar fondo blanco sólido
            const tmp = document.createElement('canvas');
            tmp.width = off.width; tmp.height = off.height;
            const tctx = tmp.getContext('2d');
            tctx.fillStyle = '#ffffff';
            tctx.fillRect(0, 0, tmp.width, tmp.height);
            tctx.drawImage(off, 0, 0);
            off.width = tmp.width; off.height = tmp.height;
            ctx.clearRect(0, 0, off.width, off.height);
            ctx.drawImage(tmp, 0, 0);
          }
          const url = off.toDataURL('image/png');
          // Reemplazar imagen manteniendo posición y escala
          const props = {
            left: img.left,
            top: img.top,
            scaleX: img.scaleX,
            scaleY: img.scaleY,
            angle: img.angle,
            customType: customType,
            transparentBackground: checked,
            codeType: codeType,
            codeValue: codeValue,
            codeFormat: codeFormat
          };
          img.set('visible', false);
          canvas.remove(img);
          window.fabric.Image.fromURL(url, (newImg) => {
            newImg.set(props);
            canvas.add(newImg);
            canvas.setActiveObject(newImg);
            newImg.setCoords();
            canvas.requestRenderAll && canvas.requestRenderAll();
          });
        };
        baseImg.src = el.src;
      } catch (e) {
        showError('No se pudo actualizar la transparencia del fondo');
      }
    };

    return (
      <div className="space-y-4 border-b border-gray-700 pb-4 mb-4">
        <div className="font-medium text-blue-400 text-sm uppercase tracking-wider mb-2">
          {customType === 'qrCode' ? 'Configuración QR' : 'Configuración Código de Barras'}
        </div>

        {customType === 'qrCode' && (
          <>
            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Tipo de QR</label>
              <select
                className="w-full px-3 py-1.5 border border-gray-600 rounded-md text-sm bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={codeType}
                onChange={(e) => handleCodeUpdate(e.target.value, codeValue, codeFormat)}
              >
                <option value="text">Texto Simple</option>
                <option value="url">Enlace (URL)</option>
                <option value="email">Correo Electrónico</option>
                <option value="vcard">Tarjeta de Contacto (vCard)</option>
              </select>
            </div>

            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>
                {codeType === 'url' ? 'URL del Sitio Web' :
                  codeType === 'email' ? 'Dirección de Correo' :
                    codeType === 'vcard' ? 'Datos del Contacto' : 'Contenido del Texto'}
              </label>
              {codeType === 'text' || codeType === 'vcard' ? (
                <textarea
                  className="w-full px-3 py-1.5 border border-gray-600 rounded-md text-sm bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  value={codeValue}
                  onChange={(e) => handleCodeUpdate(codeType, e.target.value, codeFormat)}
                  placeholder={codeType === 'vcard' ? 'BEGIN:VCARD\nVERSION:3.0\nN:Doe;John\nTEL:555-1234\nEND:VCARD' : 'Escribe aquí...'}
                />
              ) : (
                <input
                  type="text"
                  className="w-full px-3 py-1.5 border border-gray-600 rounded-md text-sm bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={codeValue}
                  onChange={(e) => handleCodeUpdate(codeType, e.target.value, codeFormat)}
                  placeholder={codeType === 'email' ? 'ejemplo@correo.com' : 'https://www.ejemplo.com'}
                />
              )}
            </div>
          </>
        )}

        {customType === 'barcode1d' && (
          <>
            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Formato</label>
              <select
                className="w-full px-3 py-1.5 border border-gray-600 rounded-md text-sm bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={codeFormat}
                onChange={(e) => handleCodeUpdate(codeType, codeValue, e.target.value)}
              >
                <option value="CODE128">CODE128 (Estándar)</option>
                <option value="CODE39">CODE39</option>
                <option value="EAN13">EAN13</option>
                <option value="UPC">UPC</option>
              </select>
            </div>
            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Valor del Código</label>
              <input
                type="text"
                className="w-full px-3 py-1.5 border border-gray-600 rounded-md text-sm bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={codeValue}
                onChange={(e) => handleCodeUpdate(codeType, e.target.value, codeFormat)}
                placeholder="1234567890"
              />
            </div>
          </>
        )}

        <label className={`flex items-center space-x-2 ${textSizes.section}`}>
          <input
            type="checkbox"
            checked={transparentBackground}
            onChange={(e) => toggleTransparent(e.target.checked)}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <span>Fondo transparente</span>
        </label>
        <div className="text-xs text-gray-500">Aplica a códigos QR y códigos de barras 1D. Convierte el blanco puro en transparente.</div>
      </div>
    );
  }, [isImageType, safeGetValue, selectedObject, textSizes.section, showError, onUpdateCode]);

  // Componentes de renderizado memoizados
  const renderPositionProperties = useCallback(() => (
    <div className="space-y-3">
      <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div>
          <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Posición X</label>
          <input
            type="number"
            className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isMobile ? 'min-h-[40px]' : ''
              }`}
            value={Math.round(Number(safeGetValue('left', 0)))}
            onChange={(e) => handleInputChange('left', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
        </div>
        <div>
          <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Posición Y</label>
          <input
            type="number"
            className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isMobile ? 'min-h-[40px]' : ''
              }`}
            value={Math.round(Number(safeGetValue('top', 0)))}
            onChange={(e) => handleInputChange('top', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
        </div>
      </div>

      {(safeGetValue('width') || safeGetValue('height')) && (
        <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {safeGetValue('width') && (
            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Ancho</label>
              <input
                type="number"
                className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isMobile ? 'min-h-[40px]' : ''
                  }`}
                value={Math.round(Number(safeGetValue('width', 0)))}
                onChange={(e) => handleInputChange('width', parseInt(e.target.value, 10))}
                onKeyDown={(e) => { e.stopPropagation(); }}
              />
            </div>
          )}
          {safeGetValue('height') && (
            <div>
              <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Alto</label>
              <input
                type="number"
                className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isMobile ? 'min-h-[40px]' : ''
                  }`}
                value={Math.round(Number(safeGetValue('height', 0)))}
                onChange={(e) => handleInputChange('height', parseInt(e.target.value, 10))}
                onKeyDown={(e) => { e.stopPropagation(); }}
              />
            </div>
          )}
        </div>
      )}

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Rotación</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="360"
            className="flex-1"
            value={Number(safeGetValue('angle', 0))}
            onChange={(e) => handleInputChange('angle', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <input
            type="number"
            min="0"
            max="360"
            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs"
            value={Math.round(Number(safeGetValue('angle', 0)))}
            onChange={(e) => handleInputChange('angle', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <span className="text-gray-500 text-xs">°</span>
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Opacidad</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            className="flex-1"
            value={Number(safeGetValue('opacity', 1))}
            onChange={(e) => handleInputChange('opacity', parseFloat(e.target.value))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <span className="text-gray-500 w-10 text-xs">
            {Math.round(Number(safeGetValue('opacity', 1)) * 100)}%
          </span>
        </div>
      </div>
    </div>
  ), [isMobile, textSizes, safeGetValue, handleInputChange]);

  const renderAppearanceProperties = useCallback(() => (
    <div className="space-y-3">
      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Color de Relleno</label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            value={safeGetValue('fill') === 'transparent' ? '#ffffff' : (safeGetValue('fill', '#ffffff'))}
            onChange={(e) => handleInputChange('fill', e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <div className="flex-1">
            <input
              type="text"
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={safeGetValue('fill', 'transparent')}
              onChange={(e) => handleInputChange('fill', e.target.value)}
              placeholder="transparent"
              onKeyDown={(e) => { e.stopPropagation(); }}
            />
          </div>
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Color de Borde</label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            value={safeGetValue('stroke', '#000000')}
            onChange={(e) => handleInputChange('stroke', e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <div className="flex-1">
            <input
              type="text"
              className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              value={safeGetValue('stroke', '#000000')}
              onChange={(e) => handleInputChange('stroke', e.target.value)}
              placeholder="#000000"
              onKeyDown={(e) => { e.stopPropagation(); }}
            />
          </div>
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Grosor del Borde</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="20"
            className="flex-1"
            value={Number(safeGetValue('strokeWidth', 0))}
            onChange={(e) => handleInputChange('strokeWidth', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <input
            type="number"
            min="0"
            max="20"
            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs"
            value={Number(safeGetValue('strokeWidth', 0))}
            onChange={(e) => handleInputChange('strokeWidth', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <span className="text-gray-500 text-xs">px</span>
        </div>
      </div>

      {safeGetValue('radius') && (
        <div>
          <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Radio del Círculo</label>
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min="5"
              max="200"
              className="flex-1"
              value={Number(safeGetValue('radius', 50))}
              onChange={(e) => handleInputChange('radius', parseInt(e.target.value, 10))}
              onKeyDown={(e) => { e.stopPropagation(); }}
            />
            <input
              type="number"
              min="5"
              max="200"
              className="w-16 px-1 py-1 border border-gray-300 rounded text-xs"
              value={Number(safeGetValue('radius', 50))}
              onChange={(e) => handleInputChange('radius', parseInt(e.target.value, 10))}
              onKeyDown={(e) => { e.stopPropagation(); }}
            />
            <span className="text-gray-500 text-xs">px</span>
          </div>
        </div>
      )}
    </div>
  ), [textSizes, safeGetValue, handleInputChange]);

  const renderTextProperties = useCallback(() => (
    <div className="space-y-3">
      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Contenido del Texto</label>
        <textarea
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
          value={safeGetValue('text', '')}
          onChange={(e) => handleInputChange('text', e.target.value)}
          rows={isMobile ? '2' : '3'}
          placeholder="Ingrese el texto aquí..."
          onFocus={() => { try { selectedObject?.exitEditing && selectedObject.exitEditing(); } catch { } }}
          onKeyDown={(e) => { e.stopPropagation(); if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation(); }}
        />
      </div>

      <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div>
          <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Tamaño de Fuente</label>
          <input
            type="number"
            min="6"
            max="200"
            className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            value={Number(safeGetValue('fontSize', 16))}
            onChange={(e) => handleInputChange('fontSize', parseInt(e.target.value, 10))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
        </div>
        <div>
          <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Altura de Línea</label>
          <input
            type="number"
            min="0.5"
            max="3"
            step="0.1"
            className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            value={Number(safeGetValue('lineHeight', 1.2))}
            onChange={(e) => handleInputChange('lineHeight', parseFloat(e.target.value))}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Familia de Fuente</label>
        <select
          className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          value={safeGetValue('fontFamily', 'Arial')}
          onChange={(e) => handleInputChange('fontFamily', e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); }}
        >
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Georgia">Georgia</option>
          <option value="Verdana">Verdana</option>
          <option value="Trebuchet MS">Trebuchet MS</option>
          <option value="Comic Sans MS">Comic Sans MS</option>
          <option value="Impact">Impact</option>
          <option value="Courier New">Courier New</option>
          <option value="monospace">Monospace</option>
        </select>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Estilo de Fuente</label>
        <div className="grid gap-1 grid-cols-3">
          <button
            className={`px-1 py-1 border rounded text-sm font-bold transition-colors ${safeGetValue('fontWeight') === 'bold'
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-gray-300 border-gray-300 hover:bg-gray-50'
              }`}
            onClick={() => handleInputChange('fontWeight',
              safeGetValue('fontWeight') === 'bold' ? 'normal' : 'bold'
            )}
          >
            B
          </button>
          <button
            className={`px-1 py-1 border rounded text-sm italic transition-colors ${safeGetValue('fontStyle') === 'italic'
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-gray-300 border-gray-300 hover:bg-gray-50'
              }`}
            onClick={() => handleInputChange('fontStyle',
              safeGetValue('fontStyle') === 'italic' ? 'normal' : 'italic'
            )}
          >
            I
          </button>
          <button
            className={`px-1 py-1 border rounded text-sm underline transition-colors ${safeGetValue('underline')
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-gray-300 border-gray-300 hover:bg-gray-50'
              }`}
            onClick={() => handleInputChange('underline', !safeGetValue('underline'))}
          >
            U
          </button>
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Alineación del Texto</label>
        <div className="grid gap-1 grid-cols-4">
          {[
            { value: 'left', icon: '≡', label: 'Izquierda' },
            { value: 'center', icon: '≣', label: 'Centro' },
            { value: 'right', icon: '≡', label: 'Derecha' },
            { value: 'justify', icon: '≋', label: 'Justificar' }
          ].map((alignOption) => (
            <button
              key={alignOption.value}
              className={`px-1 py-1 border rounded text-sm transition-colors ${safeGetValue('textAlign') === alignOption.value
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-gray-300 border-gray-300 hover:bg-gray-50'
                }`}
              onClick={() => handleInputChange('textAlign', alignOption.value)}
              title={alignOption.label}
            >
              {alignOption.icon}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className={`block font-medium text-gray-300 mb-1 ${textSizes.section}`}>Color del Texto</label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            value={safeGetValue('fill', '#000000')}
            onChange={(e) => handleInputChange('fill', e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); }}
          />
          <div className="flex-1">
            <input
              type="text"
              className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              value={safeGetValue('fill', '#000000')}
              onChange={(e) => handleInputChange('fill', e.target.value)}
              placeholder="#000000"
              onKeyDown={(e) => { e.stopPropagation(); }}
            />
          </div>
        </div>
      </div>
    </div>
  ), [textSizes, safeGetValue, handleInputChange, isMobile]);

  // Renderizado de propiedades de Data Binding
  const renderDataSourceProperties = () => {
    // Solo mostrar para texto, imágenes (códigos) y quizás formas si queremos cambiar color dinámicamente
    if (!isTextType && !isImageType) return null;

    const currentField = safeGetValue('dataField', '');
    const currentPattern = safeGetValue('textPattern', '');

    return (
      <div className="space-y-4">
        {/* Dataset Activo */}
        <div className="modern-input-group">
          <label className="modern-label flex justify-between items-center">
            <span>Dataset Activo</span>
            <div className="flex space-x-1">
              <button
                onClick={onRefreshDatasets}
                className="text-xs text-blue-400 hover:text-blue-300 p-1 rounded hover:bg-white/5"
                title="Recargar Datasets"
              >
                🔄
              </button>
            </div>
          </label>

          {selectedDataset ? (
            <div className="bg-slate-800 rounded p-2 border border-slate-700">
              <div className="text-sm font-medium text-white mb-1 truncate" title={typeof selectedDataset === 'object' ? selectedDataset.dataset_name : selectedDataset}>
                {typeof selectedDataset === 'object' ? selectedDataset.dataset_name : selectedDataset}
              </div>
              <div className="flex space-x-2 mt-2">
                <button
                  onClick={onDatabaseOpen}
                  className="flex-1 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-xs rounded transition-colors border border-blue-600/50"
                >
                  Cambiar
                </button>
                <button
                  onClick={() => onSelectDataset(null)}
                  className="flex-1 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs rounded transition-colors border border-red-600/50"
                >
                  Desvincular
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={onDatabaseOpen}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors flex items-center justify-center space-x-2"
            >
              <span>📂</span>
              <span>Seleccionar Base de Datos</span>
            </button>
          )}
        </div>

        {/* Selector de Campo o Patrón (Solo si hay dataset seleccionado) */}
        {selectedDataset && (
          <>
            {isTextType ? (
              <div className="modern-input-group">
                <label className="modern-label">Contenido Dinámico (Concatenación)</label>
                <div className="flex flex-col space-y-2">
                  <textarea
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    value={currentPattern}
                    onChange={(e) => onUpdateProperty('textPattern', e.target.value)}
                    placeholder="Ej: {Nombre} {Apellido}"
                  />
                  <div className="flex items-center space-x-2">
                    <select
                      className="modern-select text-xs flex-1"
                      onChange={(e) => {
                        if (e.target.value) {
                          const newVal = currentPattern ? `${currentPattern} {${e.target.value}}` : `{${e.target.value}}`;
                          onUpdateProperty('textPattern', newVal);
                          e.target.value = ''; // Reset
                        }
                      }}
                    >
                      <option value="">+ Insertar Campo</option>
                      {datasetFields.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-slate-500">
                    Las variables se reemplazarán por los datos del registro.
                  </p>
                </div>
              </div>
            ) : (
              <div className="modern-input-group">
                <label className="modern-label">Campo Vinculado</label>
                <select
                  className="modern-select"
                  value={currentField}
                  onChange={(e) => {
                    const field = e.target.value;
                    onUpdateProperty('dataField', field);
                  }}
                >
                  <option value="">-- Sin Vinculación --</option>
                  {datasetFields.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
                {currentField && (
                  <div className="mt-2 text-xs text-blue-400 flex items-center">
                    <span>⚡ Vinculado a: <strong>{currentField}</strong></span>
                  </div>
                )}
              </div>
            )}

            {/* Información adicional para Imágenes sobre el tipo de campo */}
            {currentField && !isTextType && selectedDataset && (
              <div className="mt-3 p-2 bg-slate-900/50 rounded border border-slate-700 text-xs">
                {(() => {
                  // Buscar ID del dataset activo
                  const datasetName = typeof selectedDataset === 'object' ? selectedDataset.dataset_name : selectedDataset;
                  const activeDatasetObj = Array.isArray(datasets) ? datasets.find(d => d.dataset_name === datasetName) : null;
                  const activeDatasetId = activeDatasetObj?._id;
                  const config = (activeDatasetId && fieldConfigs[activeDatasetId]) ? fieldConfigs[activeDatasetId][currentField] : null;

                  return (
                    <>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-400">Tipo:</span>
                        <span className={`font-medium ${config?.type === 'image_path' ? 'text-green-400' : 'text-blue-300'}`}>
                          {config?.type === 'image_path' ? 'Ruta Local' :
                            config?.type === 'image_base64' ? 'Base64' : 'Texto/URL'}
                        </span>
                      </div>

                      {config?.type === 'image_path' && (
                        <div className="mt-1">
                          <span className="block text-slate-500 text-[10px]">Ruta Base:</span>
                          ⚠️ Para usar imágenes locales, ve a <strong>Base de Datos &gt; Ver Mis Datos &gt; ⚙️</strong> y configura este campo como "Imagen (Ruta Local)".
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderLayersList = () => (
    <div className="space-y-2">
      {layers.map((layer, index) => (
        <div
          key={layer.id}
          draggable
          onDragStart={(e) => onDragStart(e, index)}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, index)}
          onClick={() => selectLayer(index)}
          className={`flex items-center p-2 rounded cursor-pointer transition-colors ${selectedLayerIndex === index ? 'bg-blue-500/20 border border-blue-500/50' : 'hover:bg-white/5 border border-transparent'
            }`}
        >
          <span className="cursor-move text-gray-500 mr-2">⋮⋮</span>
          <span className="mr-2 text-lg">{layer.obj.type === 'image' ? '🖼️' : layer.obj.text ? 'T' : '▢'}</span>
          <span className="flex-1 text-sm truncate text-gray-200">{layer.name}</span>
          <div className="flex space-x-1">
            <button
              onClick={(e) => { e.stopPropagation(); toggleVisibility(index); }}
              className={`p-1 rounded hover:bg-white/10 ${!layer.visible ? 'text-gray-600' : 'text-gray-400'}`}
              title={layer.visible ? "Ocultar" : "Mostrar"}
            >
              {layer.visible ? '👁️' : '🚫'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); toggleLock(index); }}
              className={`p-1 rounded hover:bg-white/10 ${layer.locked ? 'text-red-400' : 'text-gray-400'}`}
              title={layer.locked ? "Desbloquear" : "Bloquear"}
            >
              {layer.locked ? '🔒' : '🔓'}
            </button>
            <button
              onClick={(e) => duplicateLayer(index, e)}
              className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-blue-400"
              title="Duplicar"
            >
              📑
            </button>
            <button
              onClick={(e) => deleteLayer(index, e)}
              className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-red-400"
              title="Eliminar"
            >
              🗑️
            </button>
          </div>
        </div>
      ))}
      {layers.length === 0 && <div className="text-center text-gray-500 text-sm py-2">No hay elementos</div>}
    </div>
  );

  return (
    <aside className={`${containerClasses} ${isMinimized ? 'w-12' : ''}`}>
      {/* Header */}
      <div className="properties-header-modern">
        {!isMinimized && <h2 className={`font-bold text-white ${textSizes.header}`}>Propiedades</h2>}
        <div className="flex items-center space-x-2">
          {/* Botón de cerrar en móvil */}
          {(isMobile || isTablet) && (
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              ✕
            </button>
          )}
          {/* Botón minimizar en desktop */}
          {!isMobile && !isTablet && (
            <button onClick={() => setIsMinimized(!isMinimized)} className="text-gray-400 hover:text-white">
              {isMinimized ? '◀' : '▶'}
            </button>
          )}
        </div>
      </div>

      {isMinimized ? (
        <div className="flex flex-col items-center py-4 space-y-4">
          <span title="Capas">📚</span>
          <span title="Posición">📐</span>
          <span title="Apariencia">🎨</span>
        </div>
      ) : (
        /* Contenido Scrollable */
        <div className="properties-content-modern">
          {!selectedObject ? (
            <div className="empty-state-modern">
              <div className="empty-state-icon">👆</div>
              <div className="empty-state-text">
                Selecciona un elemento de la lista<br />o del lienzo para editar.
              </div>

              <div className="mt-4 w-full text-left">
                <div className="text-xs font-bold text-gray-400 uppercase mb-2">Elementos del Diseño</div>
                {renderLayersList()}
              </div>

              {/* Info del Lienzo cuando no hay selección */}
              <div className="mt-8 p-4 bg-white/5 rounded-lg w-full text-left">
                <div className="text-xs font-bold text-gray-400 uppercase mb-2">Información del Lienzo</div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Tamaño:</span>
                  <span className="text-white font-mono">{canvasInfo.width} x {canvasInfo.height} px</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Objetos:</span>
                  <span className="text-white font-mono">{canvasInfo.count}</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Sección: Capas */}
              <PropertySection title="Capas" icon="📚" isOpen={sectionsOpen.layers} onToggle={() => toggleSection('layers')}>
                {renderLayersList()}
              </PropertySection>

              {/* Sección: Posición y Tamaño */}
              <PropertySection title="Posición y Tamaño" icon="📐" isOpen={sectionsOpen.position} onToggle={() => toggleSection('position')}>
                {renderPositionProperties()}
              </PropertySection>

              {/* Sección: Apariencia */}
              {(isShapeType || isTextType) && (
                <PropertySection title="Apariencia" icon="🎨" isOpen={sectionsOpen.appearance} onToggle={() => toggleSection('appearance')}>
                  {renderAppearanceProperties()}
                </PropertySection>
              )}

              {/* Sección: Texto */}
              {isTextType && (
                <PropertySection title="Texto" icon="✍️" isOpen={sectionsOpen.text} onToggle={() => toggleSection('text')}>
                  {renderTextProperties()}
                </PropertySection>
              )}

              {/* Sección: Imagen */}
              {isImageType && (
                <PropertySection title="Imagen" icon="🖼️" isOpen={sectionsOpen.image} onToggle={() => toggleSection('image')}>
                  <div className="space-y-4">
                    {renderImageCodeProperties()}

                    <div className="modern-input-group">
                      <label className="modern-label">Filtros</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          className="modern-action-btn"
                          onClick={() => {
                            const filter = new fabric.Image.filters.Grayscale();
                            selectedObject.filters.push(filter);
                            selectedObject.applyFilters();
                            selectedObject.canvas.renderAll();
                          }}
                        >
                          Escala de Grises
                        </button>
                        <button
                          className="modern-action-btn"
                          onClick={() => {
                            const filter = new fabric.Image.filters.Sepia();
                            selectedObject.filters.push(filter);
                            selectedObject.applyFilters();
                            selectedObject.canvas.renderAll();
                          }}
                        >
                          Sepia
                        </button>
                      </div>
                    </div>
                  </div>
                </PropertySection>
              )}

              {/* Sección: Origen de Datos */}
              {(isTextType || isImageType) && (
                <PropertySection title="Origen de Datos" icon="💾" isOpen={sectionsOpen.dataSource} onToggle={() => toggleSection('dataSource')}>
                  {renderDataSourceProperties()}
                </PropertySection>
              )}

              {/* Sección: Acciones */}
              <PropertySection title="Acciones" icon="⚡" isOpen={sectionsOpen.actions} onToggle={() => toggleSection('actions')}>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className="modern-action-btn"
                    onClick={() => {
                      const canvas = selectedObject.canvas;
                      canvas.bringToFront(selectedObject);
                      canvas.renderAll();
                      refreshLayers();
                    }}
                    title="Traer al frente"
                  >
                    ⬆️ Al Frente
                  </button>
                  <button
                    className="modern-action-btn"
                    onClick={() => {
                      const canvas = selectedObject.canvas;
                      canvas.sendToBack(selectedObject);
                      canvas.renderAll();
                      refreshLayers();
                    }}
                    title="Enviar al fondo"
                  >
                    ⬇️ Al Fondo
                  </button>
                  <button
                    className="modern-action-btn"
                    onClick={() => {
                      const canvas = selectedObject.canvas;
                      selectedObject.clone((cloned) => {
                        canvas.add(cloned.set({
                          left: selectedObject.left + 10,
                          top: selectedObject.top + 10,
                          evented: true
                        }));
                        canvas.setActiveObject(cloned);
                        canvas.renderAll();
                      });
                    }}
                    title="Duplicar"
                  >
                    📑 Duplicar
                  </button>
                  <button
                    className="modern-action-btn text-red-400 hover:text-red-300 hover:border-red-500/50"
                    onClick={() => {
                      const canvas = selectedObject.canvas;
                      canvas.remove(selectedObject);
                      canvas.discardActiveObject();
                      canvas.renderAll();
                    }}
                    title="Eliminar"
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </PropertySection>
            </>
          )}
        </div>
      )}
    </aside>
  );
});

PropertiesPanel.displayName = 'PropertiesPanel';

export default PropertiesPanel;
